/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2018, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'./BaseTask'
], function(
	BaseTask
) {
	'use strict';

	var DataTask = BaseTask.extend({

		init: function() {
			DataTask.inherited('init', this, arguments);
			this.visualization = this.content.getFeature('Visualization');
			this.infoIndicator = this.content.getFeature('InfoIndicator');
		},

		/**
		 * Load the data and store in the data member of the renderContext.
		 * @param renderContext - information collected by a particular call to render or reRender (such as the view, control, data etc).
		 * @returns a promise resolving with the render context
		 */
		process: function(renderContext) {
			if (this._isStepComplete(renderContext, 'data')) {
				return Promise.resolve(renderContext);
			}
			this._clearDataErrorsAndWarnings();
			if (this._hasMappedSlots() && this._canExecuteQuery() && !this._hasUnavailableMetadataColumns() && !this._hasMissingFilters()) {
				return this._executeQueries(renderContext)
					.then(result => {
						if (result) {
							this.dashboardApi.triggerDashboardEvent('synchronize:pageContextFilters', { synchDataFilterEntries: result.synchDataFilterEntries });
							if( renderContext.extraInfo && renderContext.extraInfo.sameQueryValidateMode ) {
								renderContext.sameQueryData = true;
								renderContext.extraInfo.queryRefresh = true;
							}
							this._completeStep(renderContext, 'data', result);
							/**
							 * The 'newData' state (maintained by the renderSequence) is relevant in the context of the view's render() method.
							 * It means that, for a particular view.render() call, new data is being rendered.
							 * (for example, typically, render following a resize would not be newData BUT,
							 * If a resize occurs quickly after a render, 2 RenderSequence render() calls could result in one view:render() call
							 * (effecively the 2 renders are "bundled into 1" from the perspective of the view so we are newData)
							 */
							this.getRenderState().setPropertyValue('newData', true);
						}
						return renderContext;
					})
					.catch((error) => {
						if (!error || error.msg !== 'dwErrorStaleRequest'){
							var errorMessage = error ? error.msg : 'dwErrorRenderingVisualization';
							var errorParam = error? error.param : null;
							this.showError(errorMessage, errorParam);
						}
						throw error;
					});
			}
			this._completeStep(renderContext, 'data', {});
			return Promise.resolve(renderContext);
		},

		_hasMappedSlots: function() {
			return this.visualization.getSlots().getMappedSlotList().length !== 0;
		},

		/**
		 * Determine whether the query can be executed.
		 * Generally the query shall execute when all required mapping is complete.
		 * However some applications may require to execute the query regardless of the slot mapping state.
		 * The config should only affect the behaviour of the query and should have no impact on the actual render
		 * of the visualization. The render should continue to render only when isMappingComplete is true.
		 */
		_canExecuteQuery: function()  {
			return this.dashboardApi.getConfiguration('forceQuery') === true || this.visualization.getSlots().isMappingComplete();
		},

		_hasUnavailableMetadataColumns: function()  {
			return this.visualization.hasUnavailableMetadataColumns();
		},

		_hasMissingFilters: function()  {
			return this.ownerWidget && this.ownerWidget.getUnavailableLocalFilter().length > 0;
		},

		_executeQueries: function(renderContext)  {
			const queryExecution = this.content.getFeature('DataQueryExecution');
			if(renderContext.extraInfo && renderContext.extraInfo.dataQueryParams) {
				queryExecution.addRequestOptions(renderContext.extraInfo.dataQueryParams);
			}
			return queryExecution.executeQueries();
		},

		_clearDataErrorsAndWarnings() {
			// Reset any warnings/errors in the info indicator as we are starting fresh
			if (this.infoIndicator) {
				this.infoIndicator.clearMessagesWithIds(['visualization_notifications', 'moreDataIndicator', 'autobinSuggestion', 'data_notifications']);
			}
		}
	});

	return DataTask;
});
